/** Authenticated file download. A plain `<a href>` cannot work: the bearer token is attached by the axios interceptor, a link navigation bypasses axios, and the token is not a cookie, so the request would 401. So the bytes are fetched with axios and handed to the browser as a blob URL.
 *  A download, not a new tab: Teams runs the app in a sandboxed iframe where `window.open` is unreliable and a blob URL opened there has no filename. */
import { api } from './api'
import { filenameFrom } from './contentDisposition'
import { signedDownloadMethod } from '../pages/sales/signatureDownload'

/** Fetch a PDF from any authenticated endpoint and hand it to the browser as a named download. */
export async function downloadFile(
  url: string,
  fallbackName = 'document.pdf',
  language = 'mn',
  extraParams: Record<string, string> = {},
  method: 'get' | 'post' = 'get',
): Promise<void> {
  const config = {
    responseType: 'blob' as const,
    params: { disposition: 'attachment', language, ...extraParams },
  }
  const response = method === 'post' ? await api.post(url, null, config) : await api.get(url, config)

  const objectUrl = URL.createObjectURL(response.data as Blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filenameFrom(response.headers['content-disposition']) ?? fallbackName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    // Revoking immediately can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
  }
}

/** `signature` is the download-time choice for an issued document; `signer` prints that accountant's signature instead. */
export function downloadPdf(
  documentId: string,
  fallbackName = 'document.pdf',
  language = 'mn',
  signature?: 'include' | 'omit',
  signer?: string,
): Promise<void> {
  return downloadFile(
    `/api/sales/${documentId}/pdf`,
    fallbackName,
    language,
    { ...(signature ? { signature } : {}), ...(signer ? { signer } : {}) },
    signedDownloadMethod(signer),
  )
}

/** An issued document's own stored PDF as a blob URL for the preview pane. The caller owns the URL and must revoke it. */
export async function storedPdfUrl(documentId: string, language = 'mn'): Promise<string> {
  const response = await api.get(`/api/sales/${documentId}/pdf`, {
    responseType: 'blob',
    params: { disposition: 'inline', language },
  })
  return URL.createObjectURL(response.data as Blob)
}

/** Render a draft PDF from unsaved editor values as a blob URL for an <iframe>. Nothing is persisted. The caller owns the URL and must revoke it (URL.revokeObjectURL). */
export async function renderPreview(payload: unknown, language = 'mn'): Promise<string> {
  const response = await api.post('/api/sales/preview', payload, {
    responseType: 'blob',
    params: { language },
  })
  return URL.createObjectURL(response.data as Blob)
}

/** The trial new-style PDF of a saved document, re-rendered on every call and never stored. */
export function downloadNewStylePdf(documentId: string, fallbackName = 'document.pdf', language = 'mn'): Promise<void> {
  return downloadFile(`/api/sales/${documentId}/pdf`, fallbackName, language, { style: 'v2' })
}
